import { inject, isDevMode, provideAppInitializer } from '@angular/core';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { catchError, firstValueFrom, map, of, timeout } from 'rxjs';
import { getStoredLanguage } from './language-storage';
import { TranslocoHttpLoader } from './transloco-loader';

/**
 * How long bootstrap waits for the active language file before rendering anyway.
 *
 * `TranslocoService.translate()` is a synchronous in-memory lookup, so the ~500 call sites
 * across the app render the key verbatim until the file lands. Blocking bootstrap trades that
 * flash for the boot splash in `index.html`. The cap keeps a slow or unreachable asset host
 * from stalling startup indefinitely.
 *
 * This is a polish measure, not a correctness guarantee: past the cap we render anyway, so any
 * call site that resolves a key once and stores the result can still freeze a raw key on screen
 * (CP-1164). Raising the cap only narrows the window. The durable fix is to resolve keys at
 * render time — via `| transloco` or the `*transloco` directive — so a late file self-corrects.
 */
const TRANSLATION_LOAD_TIMEOUT_MS = 5000;

export function provideTranslocoConfig() {
  return [
    ...provideTransloco({
      config: {
        availableLangs: ['en', 'es'],
        defaultLang: getStoredLanguage(),
        // Only consulted by the fallback *strategy*, i.e. when the active language file
        // fails to load outright. It costs nothing on the happy path.
        fallbackLang: 'en',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        missingHandler: {
          // Deliberately off. `useFallbackTranslation` makes Transloco forkJoin the active
          // language *and* en.json on every load, so a Spanish user downloaded ~683 KB
          // instead of ~356 KB and bootstrap below would block on both. It is also
          // redundant: apps/consumer-portal/src/assets/i18n/i18n.spec.ts fails the build if
          // en.json and es.json keys diverge, so there is nothing left to fall back to.
          useFallbackTranslation: false,
          logMissingKey: true,
        },
      },
      loader: TranslocoHttpLoader,
    }),
    provideAppInitializer(() => {
      const transloco = inject(TranslocoService);

      return firstValueFrom(
        transloco.load(transloco.getActiveLang()).pipe(
          timeout(TRANSLATION_LOAD_TIMEOUT_MS),
          map(() => undefined),
          catchError(() => of(undefined)),
        ),
      );
    }),
  ];
}
